'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { emailLogin } from '@/lib/api';

export default function CuestionarioStartPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const router = useRouter();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Por favor, ingresa tu email');
      return;
    }

    if (!validateEmail(email)) {
      setError('Por favor, ingresa un email válido');
      return;
    }

    setIsLoading(true);

    try {
      await emailLogin({ email: email.trim() });

      // Store email for later use in questionnaire
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('user_email', email.trim());
      }

      // Redirect to questionnaire
      router.push('/cuestionario/evaluar');
    } catch (error) {
      //Loggear error en Sentry
      Sentry.logger.error(
        Sentry.logger.fmt`Error al generar sesión: '${error}'`
      );
      // Capture error in Sentry
      Sentry.captureException(error, {
        tags: {
          section: 'authentication',
          operation: 'email_login'
        },
        user: {
          email: email.trim()
        }
      });

      let errorMessage = 'Error al iniciar sesión. Por favor, intenta nuevamente.';

      if (error instanceof Error) {
        if (error.message.includes('conectar al servidor')) {
          errorMessage = 'No se pudo conectar al servidor. Intenta más tarde.';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Error de conexión.';
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header showBackButton backHref="/" />

      <main className="flex-1 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center">
        <div className="w-full space-y-8">
          {/* Progress Indicator */}
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                1
              </div>
              <div className="w-8 h-1 bg-gray-200"></div>
              <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold">
                2
              </div>
              <div className="w-8 h-1 bg-gray-200"></div>
              <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold">
                3
              </div>
            </div>
            <p className="text-sm text-gray-700">
              Paso 1 de 3: Información de contacto
            </p>
          </div>

          {/* Email Form */}
          <Card className="animate-fade-in">
            <CardHeader>
              <div className="text-center">
                <h1 className="text-2xl font-bold text-navy mb-2 font-display">
                  Comenzar Evaluación
                </h1>
                <p className="text-gray-700">
                  Para comenzar, necesitamos tu email para enviarte los resultados
                  de tu evaluación de competencias.
                </p>
              </div>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="email"
                  label="Correo Electrónico"
                  placeholder="tu.email@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={error}
                  helperText="Te enviaremos un reporte detallado con tus resultados"
                  required
                  autoFocus
                  autoComplete="email"
                />

                <div className="space-y-4">
                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    isLoading={isLoading}
                    disabled={!email.trim() || isLoading}
                  >
                    {isLoading ? 'Iniciando...' : 'Continuar al Cuestionario'}
                  </Button>

                  <div className="text-center">
                    <p className="text-sm text-gray-500">
                      Al continuar, aceptas que procesemos tu información de acuerdo a nuestra{' '}
                      <a href="#" className="text-primary hover:text-secondary underline">
                        política de privacidad
                      </a>
                    </p>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Info Section */}
          <div className="bg-primary/5 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-navy">
              ¿Qué esperar en esta evaluación?
            </h3>

            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium text-navy">Duración</p>
                  <p className="text-gray-700">5 minutos aproximadamente</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <p className="font-medium text-navy">Formato</p>
                  <p className="text-gray-700">Preguntas de opción múltiple</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <div>
                  <p className="font-medium text-navy">Áreas</p>
                  <p className="text-gray-700">6 competencias profesionales</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <div>
                  <p className="font-medium text-navy">Resultados</p>
                  <p className="text-blue-700">Inmediatos y enviados por email</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
